import CustomConfig from './configuration/customConfig';
import { DEFAULT_CONSENT } from './constants/cookies';
import CookieConsent from './core/cookieConsent';
import { updateCommon } from './core/dataLayer';
import { BatchManager } from './core/batchManager';
import { getCookieConsent, setCommonAttributes } from './core/commonAttributes';
import { VisitorId } from './core/visitorId';
import { addVisibilityChangeListener, addUrlChangeListener, addTriggerEventListener, handleEventsFromBeforeClientLoaded } from './core/eventHandlers';
import CookieDefinitions from '@amzn/shortbread/dist/definitions/cookie-definitions';
import { initializeVisitorIdCallback, tokenCallback, waitForUpdate } from './utils/global';
import { VisitorIdOptions } from './types/types';
import { trackPageview } from './core/eventTrackers/pageView';
import { log } from './utils/logger';
import { VISITOR_ID_REQUEST_WAIT_TIME } from './constants/global';
import initPluginManager from './utils/plugin-manager';
import { isChinaRegion } from './utils/env-utils';

// Initialize config value
CustomConfig.get();

let visitorId: VisitorId | undefined;
let batchManager: BatchManager;

/**
 * Attempts to initialize a visitor ID on the web page. It's possible that sCode may be embedded on the web page along with D2C,
 * which also initializes a visitor ID on the page. To avoid D2C overwriting the Visitor ID set by sCode, wait to see if sCode
 * is initialized on the page and sets a visitor ID before D2C Client attempts to do so.
 *
 * visitorId value should be set if sCode is not embedded on a web page, and should not be set otherwise.
 * @param consent - The cookie consent at the current point in time.
 */
async function requestVisitorId(consent: CookieDefinitions.ConsentCookie): Promise<VisitorId | undefined> {
  if (!visitorId) {
    log('Attempting to request visitor ID via D2C Client');
    visitorId = await waitForUpdate<VisitorIdOptions, VisitorId>(VISITOR_ID_REQUEST_WAIT_TIME, window.AWSMA.instanceCreated, initializeVisitorIdCallback, {
      cookieAllowed: consent.functional,
      getVisitorTokenAndHandleCallback: () => {
        log('vid created');
      },
    });
  }

  return visitorId;
}

/**
 * Sets up the Shortbread cookie consent configuration and consent change handler listener, and
 * returns the visitor's consent settings on page load.
 * @returns - The visitor's consent settings on page load.
 */
export function setupCookieConfig(): CookieDefinitions.ConsentCookie {
  const cookieConsentChangeHandler = (consent: CookieDefinitions.ConsentCookie) => {
    log(`Consent changed handler executed with consent: ${JSON.stringify(consent)}`);

    updateCommon({ cookieConsent: getCookieConsent(consent) });

    // set the d2cVisitorToken in data layer depending on cookie consent change
    AWSMA.tokenReady(tokenCallback);
  };

  CookieConsent.setConsentChangeHandler(cookieConsentChangeHandler);

  const cookieConsent = CookieConsent.cookieReader.getConsentCookie() || DEFAULT_CONSENT;

  log(`Cookie consent settings at page load: ${JSON.stringify(cookieConsent)}`);

  updateCommon({
    cookieConsent: getCookieConsent(cookieConsent),
    d2cVisitorToken: '',
  });

  return cookieConsent;
}

function setBatchManager(batchSize: number, batchTime: number, retries: number): void {
  if (!batchManager) {
    batchManager = new BatchManager({
      maxBatchSize: batchSize,
      batchingTimeMillis: batchTime,
      maxRetries: retries,
    });
  }
}

function initPageView(): void {
  setCommonAttributes(null);
  trackPageview();
}

const init = async (): Promise<void> => {
  const cookieConsent = setupCookieConfig();

  // global function available in D2C Visitor Id Client package
  AWSMA.tokenReady(tokenCallback);

  await requestVisitorId(cookieConsent);

  const batchSize = 10;
  const batchTimeInMs = 3500;
  const retries = 1;
  setBatchManager(batchSize, batchTimeInMs, retries);

  addVisibilityChangeListener(batchManager, CustomConfig.config.scrollElement);
  if (!CustomConfig.config.customPageview) {
    // TODO: Move the page view to the plugin flow
    initPageView();
    addUrlChangeListener();

    if (isChinaRegion()) {
      // Initialize the plugin manager
      initPluginManager();
    }
  }

  // stops new events from being added to eventQueue in loader script (D2CAnalyticsClient)
  window.AWSMA.initialized = true;

  addTriggerEventListener();
  handleEventsFromBeforeClientLoaded();
};

init();
